// TMOS Template Selection Modal
// Date: December 24, 2025
// Purpose: Modal for selecting templates to associate with provisioning profiles

import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { TemplateType, ProvisionScope } from '@/services/provisioningProfileService';
import { templateService, Template } from '@/services/templateService';
import { positionTemplateService } from '@/services/positionTemplateService';
import api from '@/services/api';
import { MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface TemplateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
  templateType: TemplateType;
  onSelect: (templateId: string, templateName: string, isRequired: boolean, provisionOrder: number, provisionScope: ProvisionScope) => void;
  existingTemplateIds?: string[]; // Templates already associated with this profile
  /** When set, scope is fixed (e.g. PE portfolio tenant-only provisioning). */
  lockProvisionScope?: ProvisionScope;
}

export const TemplateSelectionModal: React.FC<TemplateSelectionModalProps> = ({
  isOpen,
  onClose,
  profileId: _profileId, // Currently not used but kept for future use
  templateType,
  onSelect,
  existingTemplateIds = [],
  lockProvisionScope,
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isRequired, setIsRequired] = useState(false);
  const [provisionOrder, setProvisionOrder] = useState(0);
  const [provisionScope, setProvisionScope] = useState<ProvisionScope>('USER');

  useEffect(() => {
    if (lockProvisionScope) setProvisionScope(lockProvisionScope);
  }, [lockProvisionScope]);

  useEffect(() => {
    if (isOpen && templateType) {
      loadTemplates();
    }
  }, [isOpen, templateType]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      if (templateType === 'POSITION') {
        const positionTemplates = await positionTemplateService.list();
        const asTemplates: Template[] = positionTemplates
          .filter(pt => !existingTemplateIds.includes(pt.id))
          .map(pt => ({
            id: pt.id,
            name: pt.template_name,
            description: pt.description ?? `${pt.position_count ?? 0} positions`,
            template_type: 'POSITION'
          } as Template));
        setTemplates(asTemplates);
        return;
      }
      // Get templates by type - only PUBLIC templates for provisioning (includes PROCESS_AREAS)
      try {
        const response = await api.get(`/templates/type/${templateType}?marketplace_visibility=PUBLIC`);
        
        if (response.data.success) {
          const availableTemplates = (response.data.data || []).filter(
            (t: Template) => !existingTemplateIds.includes(t.id)
          );
          setTemplates(availableTemplates);
        } else {
          throw new Error(response.data.error || 'Failed to load templates');
        }
      } catch (fetchError: unknown) {
        console.warn('Type endpoint failed, falling back to templateService:', fetchError);
        const allTemplates = await templateService.getTemplates({
          template_type: templateType,
          marketplace_visibility: 'PUBLIC'
        } as Parameters<typeof templateService.getTemplates>[0]);
        
        const availableTemplates = allTemplates.filter(
          t => !existingTemplateIds.includes(t.id)
        );
        
        setTemplates(availableTemplates);
      }
    } catch (error: unknown) {
      const e = error as { message?: string };
      console.error('Error loading templates:', error);
      toast.error(e?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template');
      return;
    }

    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
    if (selectedTemplate) {
      onSelect(selectedTemplateId, selectedTemplate.name, isRequired, provisionOrder, provisionScope);
      // Reset form
      setSelectedTemplateId(null);
      setIsRequired(false);
      setProvisionOrder(0);
      setProvisionScope('USER');
      setSearchTerm('');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Select ${templateType} Template`}
      size="lg"
    >
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Template List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {searchTerm ? 'No templates match your search' : 'No available templates'}
            </p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto space-y-2">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                onClick={() => setSelectedTemplateId(template.id)}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedTemplateId === template.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">{template.name}</h4>
                      {selectedTemplateId === template.id && (
                        <CheckIcon className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                    {template.description && (
                      <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary">{template.template_type}</Badge>
                      {template.marketplace_visibility === 'PUBLIC' && (
                        <Badge variant="success">Public</Badge>
                      )}
                      {template.marketplace_visibility === 'PRIVATE' && (
                        <Badge variant="warning">Private</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Configuration Options */}
        {selectedTemplateId && (
          <div className="border-t pt-4 space-y-4">
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => setIsRequired(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Required template</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Required templates must be provisioned and cannot be skipped
              </p>
            </div>

            {!lockProvisionScope && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provision Scope
              </label>
              <select
                value={provisionScope}
                onChange={(e) => setProvisionScope(e.target.value as ProvisionScope)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="USER">User Level (provisioned per user)</option>
                <option value="TENANT">Tenant Level (provisioned once per tenant)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {provisionScope === 'USER' 
                  ? 'Template will be provisioned for each user with this profile'
                  : 'Template will be provisioned once for the tenant (shared across all users)'}
              </p>
            </div>
            )}
            {lockProvisionScope === 'TENANT' && (
              <p className="text-xs text-gray-600">
                Tenant level — provisioned once per portfolio company (shared across users).
              </p>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provision Order
              </label>
              <input
                type="number"
                min="0"
                value={provisionOrder}
                onChange={(e) => setProvisionOrder(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Lower numbers are provisioned first (0 = first)
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSelect}
            disabled={!selectedTemplateId || loading}
          >
            Add Template
          </Button>
        </div>
      </div>
    </Modal>
  );
};

